import { API, Auth } from 'aws-amplify'

const API_NAME = 'DonationsAPI'

// Helper to get auth headers
const getAuthHeaders = async () => {
  const session = await Auth.currentSession()
  return {
    Authorization: `Bearer ${session.getIdToken().getJwtToken()}`
  }
}

// API response types (snake_case from API)
export interface DonationTypeApiResponse {
  id: string
  name: string
  description: string
  icon_name: string
  suggested_amounts: number[]
  minimum_amount: number
  allow_custom_amount: boolean
  allow_recurring: boolean
  recurring_frequencies: string[]
  is_active: boolean
  order: number
  created_at: string
  updated_at: string
}

export interface DonationTypeCreateRequest {
  name: string
  description: string
  icon_name: string
  suggested_amounts: number[]
  minimum_amount: number
  allow_custom_amount: boolean
  allow_recurring: boolean
  recurring_frequencies: string[]
  is_active: boolean
  order: number
}

export interface DonationTypeUpdateRequest {
  name?: string
  description?: string
  icon_name?: string
  suggested_amounts?: number[]
  minimum_amount?: number
  allow_custom_amount?: boolean
  allow_recurring?: boolean
  recurring_frequencies?: string[]
  is_active?: boolean
  order?: number
}

// Frontend type (camelCase)
export interface DonationType {
  id: string
  name: string
  description: string
  iconName: string
  suggestedAmounts: number[]
  minimumAmount: number
  allowCustomAmount: boolean
  allowRecurring: boolean
  recurringFrequencies: string[]
  isActive: boolean
  order: number
  createdAt?: string
  updatedAt?: string
}

// Transform API response to frontend type
const transformApiResponse = (response: DonationTypeApiResponse): DonationType => ({
  id: response.id,
  name: response.name,
  description: response.description,
  iconName: response.icon_name || '',
  suggestedAmounts: response.suggested_amounts || [],
  minimumAmount: response.minimum_amount,
  allowCustomAmount: response.allow_custom_amount,
  allowRecurring: response.allow_recurring || false,
  recurringFrequencies: response.recurring_frequencies || [],
  isActive: response.is_active,
  order: response.order,
  createdAt: response.created_at,
  updatedAt: response.updated_at,
})

// Transform frontend type to API request
const transformToApiRequest = (donationType: DonationType): DonationTypeCreateRequest => ({
  name: donationType.name,
  description: donationType.description,
  icon_name: donationType.iconName,
  suggested_amounts: donationType.suggestedAmounts,
  minimum_amount: donationType.minimumAmount,
  allow_custom_amount: donationType.allowCustomAmount,
  allow_recurring: donationType.allowRecurring,
  recurring_frequencies: donationType.recurringFrequencies,
  is_active: donationType.isActive,
  order: donationType.order,
})

export const donationsApi = {
  // List all donation types
  async listDonationTypes(activeOnly: boolean = false): Promise<DonationType[]> {
    const path = activeOnly ? '/donation-types?active=true' : '/donation-types'
    const headers = await getAuthHeaders()
    const response: DonationTypeApiResponse[] = await API.get(API_NAME, path, { headers })
    return response.map(transformApiResponse)
  },

  // Get a single donation type by ID
  async getDonationType(id: string): Promise<DonationType> {
    const headers = await getAuthHeaders()
    const response: DonationTypeApiResponse = await API.get(API_NAME, `/donation-types/${id}`, { headers })
    return transformApiResponse(response)
  },

  // Create a new donation type
  async createDonationType(donationType: DonationType): Promise<DonationType> {
    const headers = await getAuthHeaders()
    const body = transformToApiRequest(donationType)
    const response: DonationTypeApiResponse = await API.post(API_NAME, '/donation-types', { headers, body })
    return transformApiResponse(response)
  },

  // Update an existing donation type
  async updateDonationType(id: string, updates: Partial<DonationType>): Promise<DonationType> {
    const headers = await getAuthHeaders()
    const body: DonationTypeUpdateRequest = {}

    if (updates.name !== undefined) body.name = updates.name
    if (updates.description !== undefined) body.description = updates.description
    if (updates.iconName !== undefined) body.icon_name = updates.iconName
    if (updates.suggestedAmounts !== undefined) body.suggested_amounts = updates.suggestedAmounts
    if (updates.minimumAmount !== undefined) body.minimum_amount = updates.minimumAmount
    if (updates.allowCustomAmount !== undefined) body.allow_custom_amount = updates.allowCustomAmount
    if (updates.allowRecurring !== undefined) body.allow_recurring = updates.allowRecurring
    if (updates.recurringFrequencies !== undefined) body.recurring_frequencies = updates.recurringFrequencies
    if (updates.isActive !== undefined) body.is_active = updates.isActive
    if (updates.order !== undefined) body.order = updates.order

    const response: DonationTypeApiResponse = await API.put(API_NAME, `/donation-types/${id}`, { headers, body })
    return transformApiResponse(response)
  },

  // Delete a donation type
  async deleteDonationType(id: string): Promise<void> {
    const headers = await getAuthHeaders()
    await API.del(API_NAME, `/donation-types/${id}`, { headers })
  },
}

export default donationsApi
